import { BusFront, CircleCheck, Route, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input, Select, Switch, TagInput } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import {
  ALERT_RULES,
  type AlertSettings,
  alertText,
  RULE_LABEL,
  type StaffAlert,
  useAlertSettings,
  useAlerts,
  useSaveAlertSettings,
  useTestAlert,
} from '@/lib/alerts';
import { useAuth } from '@/lib/auth';
import { formatDateTime, timeAgo } from '@/lib/format';
import { t, tn } from '@/lib/i18n';

const minutesSince = (iso: string) => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
const duration = (m: number) =>
  m < 60 ? t('{n} min', { n: m }) : t('{h} h {m} min', { h: Math.floor(m / 60), m: m % 60 });

/** Staff alerts: what is wrong right now, what was resolved lately, and who gets told. */
export function AlertsPage() {
  const { can } = useAuth();
  const [view, setView] = useState<'active' | 'recent'>('active');
  const active = useAlerts('active');
  const recent = useAlerts('recent');
  const list = view === 'active' ? active : recent;
  return (
    <>
      <PageHeader
        title={t('Alerts')}
        subtitle={t(
          'Raised automatically from the bus positions every minute. Staff can be told by e-mail or Telegram.',
        )}
      />
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <Card padded={false}>
          <div className="flex gap-1 border-b border-slate-200 px-3">
            {(['active', 'recent'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${view === v ? 'border-brand-600 font-medium text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              >
                {v === 'active' ? t('Open now') : t('Resolved (7 days)')}{' '}
                <span className="text-xs text-slate-400">
                  {(v === 'active' ? active : recent).data?.items.length ?? ''}
                </span>
              </button>
            ))}
          </div>
          {list.isLoading ? (
            <Spinner />
          ) : !list.data?.items.length ? (
            <p className="flex items-center gap-2 px-4 py-4 text-sm text-slate-600">
              <CircleCheck className="size-4 text-emerald-600" />
              {view === 'active' ? t('No open alerts.') : t('Nothing resolved in the last 7 days.')}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.data.items.map((a) => (
                <AlertRow key={a._id} a={a} />
              ))}
            </ul>
          )}
        </Card>
        {can('admin') && <SettingsCard />}
      </div>
    </>
  );
}

function AlertRow({ a }: { a: StaffAlert }) {
  const Icon = a.subject.kind === 'bus' ? BusFront : Route;
  const link = a.subject.kind === 'bus' ? `/buses/${a.subject.id}` : `/lines/${a.subject.id}`;
  return (
    <li className="flex items-start gap-3 px-4 py-2.5 text-sm">
      <Icon className={`mt-0.5 size-4 shrink-0 ${a.status === 'open' ? 'text-red-600' : 'text-slate-400'}`} />
      <div className="min-w-0 flex-1">
        <div className="text-slate-900">{alertText(a)}</div>
        <div className="text-xs text-slate-500">
          {RULE_LABEL[a.rule]} ·{' '}
          {a.status === 'open'
            ? t('for {d}', { d: duration(minutesSince(a.since)) })
            : t('{d}, resolved {when}', {
                d: duration(Math.round((Date.parse(a.resolved_at!) - Date.parse(a.since)) / 60_000)),
                when: timeAgo(a.resolved_at!),
              })}
        </div>
      </div>
      <Link to={link} className="shrink-0 font-medium text-brand-700 hover:underline">
        {a.subject.kind === 'bus' ? t('Bus') : t('Line')} →
      </Link>
    </li>
  );
}

function SettingsCard() {
  const q = useAlertSettings(true);
  const save = useSaveAlertSettings();
  const test = useTestAlert();
  const [s, setS] = useState<AlertSettings | null>(null);
  useEffect(() => {
    if (q.data && !s) setS(q.data.settings);
  }, [q.data, s]);
  if (!q.data || !s) return <Spinner />;
  const ch = q.data.channels;
  const server = q.data.settings;
  const set = (p: Partial<AlertSettings>) => setS({ ...s, ...p });
  const waiting =
    server.test_requested_at && (!server.test_result || server.test_result.at < server.test_requested_at);
  return (
    <Card title={t('Who is told, and when')}>
      <div className="space-y-4 text-sm">
        <Switch checked={s.enabled} onChange={(v) => set({ enabled: v })} label={t('Alerts on')} />
        <div>
          <div className="mb-1.5 font-medium text-slate-700">{t('Alert when … for at least')}</div>
          <ul className="space-y-1.5">
            {ALERT_RULES.map((r) => (
              <li key={r} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={s.rules[r].on}
                  onChange={(e) =>
                    set({ rules: { ...s.rules, [r]: { ...s.rules[r], on: e.target.checked } } })
                  }
                  className="rounded border-slate-300"
                  aria-label={RULE_LABEL[r]}
                />
                <span className="min-w-0 flex-1">{RULE_LABEL[r]}</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-16"
                  value={s.rules[r].minutes}
                  onChange={(e) =>
                    set({
                      rules: { ...s.rules, [r]: { ...s.rules[r], minutes: Number(e.target.value) || 1 } },
                    })
                  }
                  aria-label={t('Minutes')}
                />
                <span className="text-slate-500">min</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-slate-700">{t('Service hours')}</span>
          <Input
            className="h-8 w-20"
            value={s.service_from}
            onChange={(e) => set({ service_from: e.target.value })}
            aria-label={t('From')}
          />
          –
          <Input
            className="h-8 w-20"
            value={s.service_to}
            onChange={(e) => set({ service_to: e.target.value })}
            aria-label={t('to')}
          />
          <span className="text-xs text-slate-500">
            {t('(no signal, standing and empty lines only then)')}
          </span>
        </div>
        <div>
          <div className="mb-1 font-medium text-slate-700">{t('E-mail to')}</div>
          <TagInput
            values={s.emails}
            onChange={(v) => set({ emails: v })}
            validate={(v) => /^\S+@\S+\.\S+$/.test(v)}
            placeholder={t('name@example.com (Enter to add)')}
          />
          {!ch.email && (
            <p className="mt-1 text-xs text-amber-700">
              {t('E-mail is not set up on the server (SMTP_HOST).')}
            </p>
          )}
        </div>
        <div>
          <div className="mb-1 font-medium text-slate-700">{t('Telegram chat')}</div>
          <Input
            value={s.telegram_chat_id}
            onChange={(e) => set({ telegram_chat_id: e.target.value.trim() })}
            placeholder="-1001234567890"
            aria-label={t('Telegram chat')}
          />
          <p className="mt-1 text-xs text-slate-500">
            {ch.telegram
              ? t('Add the bot to the staff group, then put the group’s chat id here.')
              : t('Telegram is not set up on the server (TELEGRAM_BOT_TOKEN).')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.notify_resolved}
              onChange={(e) => set({ notify_resolved: e.target.checked })}
              className="rounded border-slate-300"
            />
            {t('Also tell when resolved')}
          </label>
          <div className="flex items-center gap-2">
            {t('Language')}
            <Select
              className="h-8 w-28"
              aria-label={t('Language')}
              value={s.language}
              onChange={(e) => set({ language: e.target.value as 'sq' | 'en' })}
            >
              <option value="sq">Shqip</option>
              <option value="en">English</option>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
          <Button
            loading={save.isPending}
            onClick={() => {
              const { test_requested_at: _a, test_result: _b, ...input } = s;
              save.mutate(input);
            }}
          >
            {t('Save')}
          </Button>
          <Button
            variant="secondary"
            icon={<Send className="size-4" />}
            disabled={!!waiting}
            onClick={() => test.mutate()}
          >
            {waiting ? t('Sending…') : t('Send a test')}
          </Button>
        </div>
        {server.test_result && !waiting && (
          <p className={`text-xs ${server.test_result.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {server.test_result.ok
              ? t('Test sent by {channels} at {time}.', {
                  channels: server.test_result.message,
                  time: formatDateTime(server.test_result.at),
                })
              : t('Test failed: {message}', { message: server.test_result.message })}
          </p>
        )}
        <p className="text-xs text-slate-500">
          {tn(s.emails.length, '{n} e-mail address', '{n} e-mail addresses')}
          {s.telegram_chat_id ? ` · Telegram` : ''}
        </p>
      </div>
    </Card>
  );
}
